import type { ReactNode } from 'react'
import { toastManager } from '@systemeio/components'
import { useTranslation } from 'next-i18next/pages'
import Head from 'next/head'
import { useRouter } from 'next/router'
import useUser from 'shared/hooks/use-user'
import { PostAttachmentsProvider } from 'modules/attachments/components/context'
import { useDeletePost } from 'modules/community/api/use-delete-post'
import { useDeletePostAndBanUser } from 'modules/community/api/use-delete-post-and-ban'
import { usePostModeration } from 'modules/community/api/use-post-moderation'
import { useToggleLike } from 'modules/community/api/use-toggle-like'
import { PostAnnounceSkeleton } from 'modules/community/components/post-announce/components/post-announce-skeleton'
import { shouldHideUnderModerationContent } from 'modules/community/components/under-moderation-placeholder'
import type { PostInterface } from 'modules/community/types/post-interface'
import { PostView } from './components/post-view'

const PostDetails = ({ post }: { post: PostInterface }): ReactNode => {
  const router = useRouter()
  const { communityPath, commentId } = router.query
  const { user } = useUser()
  const { t } = useTranslation()
  const hideContent = shouldHideUnderModerationContent(post.underModeration, user)

  const deletePost = useDeletePost()
  const deletePostAndBan = useDeletePostAndBanUser({
    communityPath: communityPath,
  })
  const toggleLike = useToggleLike()
  const approvePost = usePostModeration({
    communityPath: communityPath as string,
    target: 'approve',
    onError: () =>
      toastManager.add({ type: 'error', title: t('post_details.error.error_approve_post') }),
  })
  const declinePost = usePostModeration({
    communityPath: communityPath as string,
    target: 'decline',
    onError: () =>
      toastManager.add({ type: 'error', title: t('core.error.internal_error_message') }),
  })

  const handleDeletePost = async (): Promise<void> => {
    await deletePost(post.id)
    await router.push(`/community/${communityPath}`)
  }

  const handleApprovePost = (): void => {
    approvePost.mutate(post.id)
  }

  const handleDeclinePost = (): void => {
    declinePost.mutate(post.id)
  }

  const handleDeletePostAndBan = async (removeRecentActivities: boolean): Promise<void> => {
    await deletePostAndBan({
      postId: post.id,
      userId: post.userId,
      removeRecentActivities,
    })
    await router.push(`/community/${communityPath}`)
  }

  if (!user) {
    return <PostAnnounceSkeleton />
  }

  return (
    <PostAttachmentsProvider attachments={post.attachments}>
      <Head>
        <title>{hideContent ? t('community.moderation.content.post_hidden') : post.title}</title>
      </Head>
      <div className="mt-6 bg-bg-primary lg:mt-0 lg:rounded-lg">
        <PostView
          post={post}
          handleApprovePost={handleApprovePost}
          handleDeclinePost={handleDeclinePost}
          handleDeletePost={handleDeletePost}
          handleLikeClick={() => toggleLike.mutate({ id: post.id, isLiked: !post.isLiked })}
          deletePostAndBanUser={handleDeletePostAndBan}
          isLoading={approvePost.isPending || declinePost.isPending}
          targetCommentId={commentId ? Number(commentId) : undefined}
        />
      </div>
    </PostAttachmentsProvider>
  )
}

export default PostDetails
